import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Login } from "./components/Login";
import { Register } from "./components/Register";
import { EmailVerification } from "./components/EmailVerification";
import { UnifiedConsole } from "./components/UnifiedConsole";
import { ProjectPage } from "./components/ProjectPage";
import { CSVProcessor } from "./components/CSVProcessor";
import { DataRequestForm } from "./components/DataRequestForm";
import { YourRequestsPage } from "./components/YourRequestsPage";
import { DatasetBrowser } from "./components/DatasetBrowser";
import { AdminDashboard } from "./components/AdminDashboard";
import { TwoFactorSetup } from "./components/TwoFactorSetup";
import { ForgotPassword } from "./components/ForgotPassword";
import { ResetPassword } from "./components/ResetPassword";
import { ProfileSettings } from "./components/ProfileSettings";
import { AccessRequestManager } from "./components/AccessRequestManager";
import { MyAccessRequests } from "./components/MyAccessRequests";
import { NotificationListPage } from "./components/NotificationListPage";
import { AdminNotificationCenter } from "./components/AdminNotificationCenter";
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import DataSourcePortal from "./components/DataSourcePortal";
import { PublicLayout } from "./components/public/PublicLayout";
import { HomePage } from "./components/public/HomePage";
import { AboutPage } from "./components/public/AboutPage";
import { SecurityPage } from "./components/public/SecurityPage";
import { RoadmapPage } from "./components/public/RoadmapPage";
import { apiService } from "./services/api";
import { User } from "./types/api";
import { isPublicSitePath } from "./utils/publicPaths";

function RequireAuth({ children, isAuthenticated }: { children: React.ReactElement; isAuthenticated: boolean }) {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function CSVProcessorRoute() {
  const { id } = useParams();
  const location = useLocation();
  const projectId = parseInt(id || "0", 10);
  const projectTitle = location.state?.projectTitle || "Project";

  if (!projectId) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <CSVProcessor
      projectId={projectId}
      projectTitle={projectTitle}
      userRole="researcher"
    />
  );
}

function OldRequestDetailRedirect() {
  const { requestId } = useParams();
  return <Navigate to={`/requests/${requestId}`} replace />;
}

function RequestsRoute({ user, isAdmin }: { user: User | null; isAdmin: boolean }) {
  const { requestId } = useParams();
  const id = requestId ? parseInt(requestId, 10) : undefined;

  if (isAdmin && id) {
    return <Navigate to={`/admin/requests/${id}`} replace />;
  }
  return <YourRequestsPage user={user} selectedId={id} />;
}

function DataRequestNewRoute({ user }: { user: User | null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedDataset = location.state?.preselectedDataset || null;
  const preselectedProjectId = location.state?.preselectedProjectId || undefined;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <DataRequestForm
        preselectedDataset={preselectedDataset}
        preselectedProjectId={preselectedProjectId}
        user={user}
        onRequestCreated={() => {
          navigate("/requests");
        }}
      />
    </div>
  );
}

function ProjectPageRoute({ user, isPIAnywhere }: { user: User | null; isPIAnywhere: boolean }) {
  const { id } = useParams();
  const projectId = parseInt(id || "0", 10);

  if (!projectId) {
    return <Navigate to="/projects" replace />;
  }

  return <ProjectPage projectId={projectId} user={user} isPIAnywhere={isPIAnywhere} />;
}

function EmailVerificationRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || localStorage.getItem("registration_email") || "";

  return (
    <EmailVerification
      email={email}
      onVerificationSuccess={() => navigate("/login")}
      onCancel={() => {
        localStorage.removeItem("registration_email");
        navigate("/login");
      }}
    />
  );
}

function ResetPasswordRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = location.state?.token || new URLSearchParams(window.location.search).get("token") || "";

  return (
    <ResetPassword
      token={token}
      onSuccess={() => navigate("/login")}
      onBack={() => navigate("/login")}
    />
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isPIAnywhere, setIsPIAnywhere] = useState(false);
  const [hasProjects, setHasProjects] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    apiService.setOnSessionExpired(() => {
      if (isAuthenticatedRef.current) {
        setShowSessionExpiredModal(true);
      }
    });

    // Check for token in URL parameters - route to correct page based on path
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const isApiPath = window.location.pathname.startsWith("/api/");

    if (token && !isApiPath) {
      const path = window.location.pathname;
      if (path === "/register" || path === "/accept-invitation" || path === "/upload") {
        // Invitation token - let the Register component handle it
        navigate("/register", { state: { token }, replace: true });
        setInitializing(false);
        return;
      } else if (path === "/reset-password") {
        // Password reset token
        navigate("/reset-password", { state: { token }, replace: true });
        setInitializing(false);
        return;
      }
      // For any other path with a token, default to reset-password for backward compat
      window.history.replaceState({}, document.title, "/reset-password");
      navigate("/reset-password", { state: { token }, replace: true });
      setInitializing(false);
      return;
    }

    if (apiService.isAuthenticated()) {
      setIsAuthenticated(true);
      apiService
        .getProfile()
        .then((profile) => {
          setUser(profile.user);
          setIsPIAnywhere(profile.is_pi_anywhere);
          setHasProjects((profile.projects ?? []).length > 0);
          // Only redirect if on root or login page
          if (location.pathname === "/" || location.pathname === "/login") {
            let target = "/projects";
            if (profile.user.role === "data_uploader") target = "/data-source";
            else if (profile.user.role === "manager" || profile.is_pi_anywhere) target = "/admin";
            navigate(target, { replace: true });
          }
        })
        .catch((error) => {
          if (error.message && (error.message.includes("2FA") || error.message.includes("setup"))) {
            navigate("/2fa-setup", { replace: true });
          } else {
            console.error("Failed to load user profile:", error);
            if (location.pathname === "/" || location.pathname === "/login") {
              navigate("/projects", { replace: true });
            }
          }
        })
        .finally(() => setInitializing(false));
    } else {
      const publicRoutes = ["/login", "/register", "/verify-email", "/forgot-password", "/reset-password", "/accept-invitation", "/upload"];
      if (!isPublicSitePath(location.pathname) && !publicRoutes.some(r => location.pathname.startsWith(r))) {
        navigate("/login", { replace: true });
      }
      setInitializing(false);
    }
  }, []);

  const handleLoginSuccess = async () => {
    setIsAuthenticated(true);
    try {
      const profile = await apiService.getProfile();
      setUser(profile.user);
      setIsPIAnywhere(profile.is_pi_anywhere);
      setHasProjects(profile.projects.length > 0);
      const targetRoute = profile.user.role === "data_uploader" ? "/data-source" :
                         profile.user.role === "manager" || profile.is_pi_anywhere ? "/admin" :
                         "/projects";
      navigate(targetRoute);
    } catch (error) {
      console.error("Failed to load user profile:", error);
      navigate("/login");
    }
  };

  const handleRequires2FASetup = () => {
    setIsAuthenticated(true);
    navigate("/2fa-setup");
  };

  const handle2FASetupComplete = () => {
    handleLoginSuccess();
  };

  const handleLogout = () => {
    apiService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setIsPIAnywhere(false);
    setHasProjects(false);
    navigate("/login");
  };

  const handleRegisterSuccess = (email: string) => {
    navigate("/verify-email", { state: { email } });
  };

  const handleSessionExpired = () => {
    setShowSessionExpiredModal(false);
    handleLogout();
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const isAdmin = user?.role === "manager" || isPIAnywhere;
  const isDataUploader = user?.role === "data_uploader";
  const defaultAuthRoute = isDataUploader ? "/data-source" : isAdmin ? "/admin" : "/projects";

  return (
    <>
      <PublicLayout
        isAuthenticated={isAuthenticated}
        dashboardRoute={defaultAuthRoute}
        user={user}
        isPIAnywhere={isPIAnywhere}
        hasProjects={hasProjects}
        isAdmin={isAdmin}
        onLogout={handleLogout}
      >
        <Routes>
          {/* Public site pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/roadmap" element={<RoadmapPage />} />

          {/* Public routes */}
          <Route path="/login" element={
            isAuthenticated ?
              <Navigate to={defaultAuthRoute} replace /> :
              <Login
                onLoginSuccess={handleLoginSuccess}
                onRequires2FASetup={handleRequires2FASetup}
              />
          } />
          <Route path="/register" element={
            isAuthenticated ?
              <Navigate to={defaultAuthRoute} replace /> :
              <Register onRegisterSuccess={handleRegisterSuccess} onRequires2FASetup={handleRequires2FASetup} onInvitationLoginSuccess={handleLoginSuccess} />
          } />
          <Route path="/verify-email" element={<EmailVerificationRoute />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPasswordRoute />} />

          {/* Auth required routes */}
          <Route path="/2fa-setup" element={
            <TwoFactorSetup
              onSetupComplete={handle2FASetupComplete}
              onCancel={() => navigate(defaultAuthRoute)}
            />
          } />
          <Route path="/projects" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              {isDataUploader ? (
                <Navigate to="/data-source" replace />
              ) : isAdmin ? (
                // Admins/PIs/managers already get the same console at /admin's
                // Overview tab (inside the admin nav shell) — redirect here so
                // there's exactly one real console URL per viewer, not a bare
                // copy at /projects with no link back to the shell version.
                <Navigate to="/admin" replace />
              ) : (
                <div className="max-w-7xl mx-auto px-4 py-8">
                  <UnifiedConsole
                    currentUserId={user?.id}
                    currentUserName={user?.name}
                    currentUserRole={user?.role}
                  />
                </div>
              )}
            </RequireAuth>
          } />
          <Route path="/projects/:id" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <ProjectPageRoute user={user} isPIAnywhere={isPIAnywhere} />
            </RequireAuth>
          } />
          <Route path="/projects/:id/csv" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <CSVProcessorRoute />
            </RequireAuth>
          } />
          <Route path="/requests" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <RequestsRoute user={user} isAdmin={isAdmin} />
            </RequireAuth>
          } />
          <Route path="/requests/new" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <DataRequestNewRoute user={user} />
            </RequireAuth>
          } />
          {/* Detail: notification links + clickable list items. Admins land on
              the admin-style detail view; requesters get the split-view list
              with this request pre-selected. */}
          <Route path="/requests/:requestId" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <RequestsRoute user={user} isAdmin={isAdmin} />
            </RequireAuth>
          } />
          {/* Old paths, kept for bookmarks and any stale notification links */}
          <Route path="/data-requests" element={<Navigate to="/requests" replace />} />
          <Route path="/data-requests/new" element={<Navigate to="/requests/new" replace />} />
          <Route path="/data-requests/:requestId" element={<OldRequestDetailRedirect />} />
          <Route path="/datasets" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <DatasetBrowser />
            </RequireAuth>
          } />
          {/* Must come before /admin/* below — otherwise that wildcard route
              matches first and this one is never reachable. */}
          <Route path="/admin/access-requests" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <AccessRequestManager />
            </RequireAuth>
          } />
          <Route path="/admin/*" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <div className="max-w-7xl mx-auto px-4 py-8">
                <AdminDashboard user={user} />
              </div>
            </RequireAuth>
          } />
          <Route path="/data-source/*" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <DataSourcePortal />
            </RequireAuth>
          } />
          <Route path="/my-requests" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <MyAccessRequests />
            </RequireAuth>
          } />
          <Route path="/notifications" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <NotificationListPage canCompose={isAdmin} />
            </RequireAuth>
          } />
          <Route path="/notifications/compose" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <AdminNotificationCenter
                isOrgAdmin={user?.role === "manager"}
                isPIAnywhere={isPIAnywhere}
              />
            </RequireAuth>
          } />
          <Route path="/settings" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <ProfileSettings />
            </RequireAuth>
          } />

          {/* Catch-all redirect */}
          <Route path="*" element={
            <Navigate to={isAuthenticated ? defaultAuthRoute : "/"} replace />
          } />
        </Routes>
      </PublicLayout>

      {showSessionExpiredModal && (
        <SessionExpiredModal onClose={handleSessionExpired} />
      )}
    </>
  );
}

export default App;
